import type { Question, QuestionSubType } from '@mentimeter/http-clients';
import { usePresentationContext } from '@mentimeter/question-modules-contexts';
import React from 'react';
import { Box } from '@mentimeter/ragnar-ui/box';
import type { BoxT } from '@mentimeter/ragnar-ui/box';
import { FlowingGrid } from './FlowingGrid';
import { SpeechBubbles } from './SpeechBubbles';
import { FocusedView } from './FocusedView';
import type { VisualizationState } from './utils/getVisualizationState';
import { useOpenSlideState } from './utils/useOpenSlideState';
import {
  NUM_COLUMNS_WITH_IMAGE_LAYOUT,
  NUM_COLUMNS_WITHOUT_IMAGE_LAYOUT,
} from './utils/constants';
import type { OpenEndedResponse } from './types';

interface VisualizationProps extends BoxT {
  responses: OpenEndedResponse[];
  subType: QuestionSubType;
  visualizationState: VisualizationState;
  layout: Question['layout'];
}

export const Visualization = ({
  subType,
  visualizationState,
  responses,
  layout,
  ...rest
}: VisualizationProps) => {
  const { useConfig, track } = usePresentationContext();
  const { enableAnimations, readonly } = useConfig();
  const [{ activeResponseIndex }, setSlideState] = useOpenSlideState();

  const showUpvotes = visualizationState === 'view-results';
  const sortedResponses = [...responses].sort(
    (a, b) => b.voteCount - a.voteCount,
  );
  const displayResponses = showUpvotes ? sortedResponses : responses;
  const prevVisualizationStateRef = React.useRef<VisualizationState | null>(
    null,
  );

  const handleSetActiveResponseIndex = React.useCallback(
    (index: number | null) => {
      setSlideState({ activeResponseIndex: index });
    },
    [setSlideState],
  );

  const handleOpenOneByOneView = (index: number) => {
    handleSetActiveResponseIndex(index);
    track({
      event: 'Show one by one answer',
      properties: {
        placement: 'Presentation view menu',
        context: 'Presentation view',
      },
    });
  };

  const showOneByoneView =
    activeResponseIndex !== null &&
    activeResponseIndex !== undefined &&
    displayResponses.length > 0;

  React.useEffect(() => {
    // show the results view (not one by one) if arriving at 'view-results'
    // state and an activeResponseIndex is selected since before
    if (
      visualizationState === 'view-results' &&
      prevVisualizationStateRef.current !== visualizationState &&
      showOneByoneView
    ) {
      handleSetActiveResponseIndex(null);
    }
    prevVisualizationStateRef.current = visualizationState;
  }, [handleSetActiveResponseIndex, showOneByoneView, visualizationState]);

  if (showOneByoneView) {
    return (
      <FocusedView
        onDismiss={() => handleSetActiveResponseIndex(null)}
        currentIndex={activeResponseIndex ?? 0}
        responses={displayResponses}
        setCurrentIndex={handleSetActiveResponseIndex}
        showUpvotes={showUpvotes}
        readonly={readonly}
      />
    );
  }

  const hasSideImageLayout =
    layout?.type === 'right' || layout?.type === 'left';
  const numberOfColumns = hasSideImageLayout
    ? NUM_COLUMNS_WITH_IMAGE_LAYOUT
    : NUM_COLUMNS_WITHOUT_IMAGE_LAYOUT;

  const shouldShowFlowingGrid = subType === 'flow' && enableAnimations;

  return (
    <Box flex="1 1 auto" width="100%" height="100%" {...rest}>
      {shouldShowFlowingGrid ? (
        <FlowingGrid
          showUpvotes={showUpvotes}
          responses={displayResponses}
          onItemClick={handleOpenOneByOneView}
          numberOfColumns={numberOfColumns}
        />
      ) : (
        <SpeechBubbles
          showUpvotes={showUpvotes}
          responses={displayResponses}
          onItemClick={handleOpenOneByOneView}
          numberOfColumns={numberOfColumns}
        />
      )}
    </Box>
  );
};
